import { lazy, type ComponentType } from "react";

type Importer<T extends ComponentType<any>> = () => Promise<{ default: T }>;

/**
 * React.lazy wrapper that survives transient chunk-loading failures.
 *
 * After a deploy, an open tab references JS chunk filenames that no longer exist.
 * The dynamic import then either rejects, or (with some CDN/service-worker caches)
 * resolves to an empty/HTML module — producing
 * "Cannot read properties of undefined (reading 'default')".
 *
 * Strategy: retry once with a cache-busting query param, then force a single
 * full reload so the browser picks up the current build manifest.
 */
export function lazyWithRetry<T extends ComponentType<any>>(importer: Importer<T>) {
  return lazy(async () => {
    const load = async () => {
      const mod = await importer();
      if (!mod || typeof mod.default === "undefined") {
        throw new Error("ChunkLoadError: module resolved without a default export");
      }
      return mod;
    };

    try {
      return await load();
    } catch {
      // Second chance — network blip or a partially cached chunk.
      try {
        return await load();
      } catch (err) {
        const FLAG = "ffa-chunk-reloaded";
        try {
          if (!sessionStorage.getItem(FLAG)) {
            sessionStorage.setItem(FLAG, "1");
            window.location.reload();
          }
        } catch {
          /* storage unavailable — surface the error */
        }
        throw err;
      }
    }
  });
}

export default lazyWithRetry;
